import { isUndefined } from 'lodash-es';
import type { IntlShape } from 'react-intl';

import type { MinMax } from '../../../core/types/min-max.js';
import type { XYChartSelectionRectangleMetadata } from '../../hooks/useSortRectangleDatapoints.js';
import { XYAxisTypeCategorical } from '../../types/axis.js';
import type {
  AnyDatapoint,
  DatapointMetadata,
  XYChartAxisInternal,
  XYChartSeriesWithDatapoints,
  XYChartVariantsInternal,
} from '../../types/xy-chart-internals.js';
import type { XYChartSelectionLinearMetadata } from '../../types/xy-chart-selection.js';
import type { XYChartColorPalette, XYChartData } from '../../types/xy-chart.js';
import { calculateAxesDomains } from '../../utils/calculate-axes-domains.js';
import type {
  ControlFlowCommand,
  MutableProcessingContext,
  SeriesConstructor,
  SeriesProcessingStrategy,
} from '../types/processing-types.js';
import {
  getSeriesNameWithAccessor,
  getSeriesNameWithFallback,
} from '../utils/series-naming/get-series-name.js';

export abstract class SeriesBaseStrategy<
  T extends XYChartSelectionLinearMetadata | XYChartSelectionRectangleMetadata,
> implements SeriesProcessingStrategy
{
  protected readonly addValue: (point: T) => void;

  constructor(initializer: SeriesConstructor<T>) {
    this.addValue = initializer.addValue;
  }

  abstract canHandle(seriesProps: XYChartVariantsInternal): boolean;

  public preprocess(
    data: XYChartData[],
    seriesProps: XYChartVariantsInternal,
    xAxis: XYChartAxisInternal,
    yAxis: XYChartAxisInternal,
    context: MutableProcessingContext,
  ): ControlFlowCommand {
    if (!this.canHandle(seriesProps)) {
      return;
    }
    return this.preprocessImpl(data, seriesProps, xAxis, yAxis, context);
  }

  public process(
    datum: XYChartData,
    seriesProps: XYChartVariantsInternal,
    xAxis: XYChartAxisInternal,
    yAxis: XYChartAxisInternal,
    context: MutableProcessingContext,
  ): ControlFlowCommand {
    if (!this.canHandle(seriesProps)) {
      return;
    }

    const datapoint = this.getDatapoint(datum, seriesProps, xAxis, yAxis);
    if (!datapoint) {
      return 'continue';
    }

    return this.processImpl(
      datum,
      seriesProps,
      xAxis,
      yAxis,
      datapoint,
      context,
    );
  }

  protected preprocessImpl(
    _data: XYChartData[],
    _seriesProps: XYChartVariantsInternal,
    _xAxis: XYChartAxisInternal,
    _yAxis: XYChartAxisInternal,
    _context: MutableProcessingContext,
  ): ControlFlowCommand {
    return;
  }

  protected abstract processImpl(
    datum: XYChartData,
    seriesProps: XYChartVariantsInternal,
    xAxis: XYChartAxisInternal,
    yAxis: XYChartAxisInternal,
    datapoint: AnyDatapoint,
    context: MutableProcessingContext,
  ): ControlFlowCommand;

  /**
   * Gets the series ID for this datum. Override in subclasses for custom ID logic.
   */
  protected abstract getSeriesId(
    datum: XYChartData,
    seriesProps: XYChartVariantsInternal,
  ): string;

  /**
   * Gets or creates the series index, adding to seriesIndexes if new.
   */
  protected getOrCreateSeriesIndex(
    seriesId: string,
    seriesIndexes: string[],
  ): number {
    if (!seriesIndexes.includes(seriesId)) {
      seriesIndexes.push(seriesId);
    }
    return seriesIndexes.indexOf(seriesId);
  }

  /**
   * Creates a new series object. Override in subclasses for custom series structure.
   */
  protected createSeries(
    seriesId: string,
    seriesProps: XYChartVariantsInternal,
    xAxis: XYChartAxisInternal,
    yAxis: XYChartAxisInternal,
  ) {
    return {
      id: seriesId,
      type: seriesProps.type,
      data: [],
      xAxisId: xAxis.id,
      yAxisId: yAxis.id,
    };
  }

  /**
   * Registers a new series in the context maps.
   */
  protected registerSeries(
    currentSeries: XYChartSeriesWithDatapoints,
    seriesId: string,
    context: MutableProcessingContext,
  ): void {
    context.seriesMap.set(seriesId, currentSeries);
    context.seriesWithDatapoints.push(currentSeries);
  }

  /**
   * Registers a datapoint to the series and updates metadata and domains.
   */
  protected registerDatapoint<
    D extends AnyDatapoint,
    Y extends XYChartSeriesWithDatapoints & { data: D[] },
  >(
    datapoint: D,
    datapointMetadata: DatapointMetadata,
    currentSeries: Y,
    context: MutableProcessingContext,
  ) {
    currentSeries.data.push(datapoint);
    context.datapointMetadataMap.set(datapoint, datapointMetadata);
  }

  /**
   * Creates series metadata. Override in subclasses for custom metadata.
   */
  protected abstract createSeriesMetadata(
    datum: XYChartData,
    seriesProps: XYChartVariantsInternal,
    xAxis: XYChartAxisInternal,
    yAxis: XYChartAxisInternal,
    seriesId: string,
    context: MutableProcessingContext,
  ): Record<string, unknown>;

  /**
   * Calculates and sets the axis domains.
   */
  protected updateAxisDomains(
    calcPoint: AnyDatapoint,
    xAxis: XYChartAxisInternal,
    yAxis: XYChartAxisInternal,
    axisIdToScaleDomain: MutableProcessingContext['axisIdToScaleDomain'],
    isSeriesVisible: boolean,
  ): void {
    const { xDomain, yDomain } = calculateAxesDomains(
      xAxis,
      yAxis,
      calcPoint,
      axisIdToScaleDomain,
      isSeriesVisible,
    );
    axisIdToScaleDomain.set(xAxis.id, xDomain);
    if (isSeriesVisible || yAxis.type === XYAxisTypeCategorical) {
      axisIdToScaleDomain.set(yAxis.id, yDomain as MinMax | Set<string>);
    }
  }

  /**
   * Gets the calculation point for domain updates. Override for custom logic (e.g., time centering).
   */
  protected getCalculationPoint(
    datapoint: AnyDatapoint,
    _xAxis: XYChartAxisInternal,
    _yAxis: XYChartAxisInternal,
  ): { x0?: unknown; y0?: unknown; x1?: unknown; y1?: unknown } {
    return datapoint;
  }

  getSeriesName(
    intl: IntlShape,
    seriesIndex: number,
    seriesNameFallback: string | undefined,
    namingWithAccessor?: {
      seriesNameAccessor?: string;
      datum: XYChartData;
    },
  ) {
    return isUndefined(namingWithAccessor)
      ? getSeriesNameWithFallback(intl, seriesIndex, seriesNameFallback)
      : getSeriesNameWithAccessor(
          intl,
          seriesIndex,
          seriesNameFallback,
          namingWithAccessor.seriesNameAccessor,
          namingWithAccessor.datum,
        );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  abstract getColor(...args: any[]):
    | {
        color: { Default: string };
        colorPalette: XYChartColorPalette;
        colorIsFromSlot: boolean;
      }
    | {
        colorPalette: XYChartColorPalette;
        colorIsFromSlot: boolean;
      };

  abstract getDatapoint(
    datum: XYChartData,
    seriesProps: XYChartVariantsInternal,
    xAxis: XYChartAxisInternal,
    yAxis: XYChartAxisInternal,
  ): AnyDatapoint | undefined;
}
